import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { AssistantHint } from 'src/components/assistant-hint'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { TraTokenLink } from 'src/components/properties/tra-token-link'
import { isAxiosError } from 'axios'
import { toast } from 'react-toastify'
import i18next from 'i18next'
import { Pagination } from '@thinknimble/tn-models'
import { Button } from 'src/components/button'
import { Input } from 'src/components/input'
import { CustomSelect } from 'src/components/custom-select'
import { propertyApi } from 'src/services/properties/api'
import { propertyQueries } from 'src/services/properties/queries'
import type { Property } from 'src/services/properties/models'
import type { SelectOption } from 'src/services/base-model'
import { cn } from 'src/utils/style'
import { reportingGuidePath } from 'src/content/help'
import { InfoHint } from 'src/components/info-hint'

type PropertyReportingSettingsProps = {
  property: Property
}

type ReportingFormState = {
  traEnabled: boolean
  traApiKey: string
  traRntCode: string
  traEstablishmentName: string
  traAccommodationType: string
  sireEnabled: boolean
  sireEstablishmentCode: string
  sireCityCode: string | null
}

/**
 * Valid values for TRA's `tipo_acomodacion` payload field (Resolución 409/2022).
 * Presented as a select in the UI. An empty string means "use the default
 * (`Apartamento`)" — see `_tra_data()` on the backend.
 */
const ACCOMMODATION_TYPES: { value: string; label: string }[] = [
  { value: '', label: '' },
  { value: 'Apartamento', label: 'Apartamento' },
  { value: 'Casa', label: 'Casa' },
  { value: 'Hotel', label: 'Hotel' },
  { value: 'Hostal', label: 'Hostal' },
]

const initialFromProperty = (property: Property): ReportingFormState => ({
  traEnabled: Boolean(property.traEnabled),
  traApiKey: '',
  traRntCode: property.traRntCode ?? '',
  traEstablishmentName: property.traEstablishmentName ?? '',
  traAccommodationType: property.traAccommodationType ?? '',
  sireEnabled: Boolean(property.sireEnabled),
  sireEstablishmentCode: property.sireEstablishmentCode ?? '',
  sireCityCode: property.sireCityCode ?? null,
})

/**
 * Per-property Reporting Settings card.
 *
 * Only rendered for Colombian properties. Lets users enable/disable TRA and
 * SIRE reporting and edit the associated credentials. The API key is never
 * re-exposed — we show a masked placeholder (last 4 chars) and only send
 * the new value if the user typed one.
 *
 * The "Copy from another property" helper lists other properties in the same
 * organization that already have reporting set, and prefills the form
 * (without saving) when one is selected.
 */
export const PropertyReportingSettings = ({ property }: PropertyReportingSettingsProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [form, setForm] = useState<ReportingFormState>(() => initialFromProperty(property))

  const isColombia = property.country === 'CO'
  const hasExistingMaskedKey = Boolean(property.traApiKeyMasked)
  const ownerId = property.owner ?? undefined

  // Re-sync from server when the property changes (e.g. after save refetch).
  useEffect(() => {
    setForm(initialFromProperty(property))
  }, [property])

  // Siblings (same org, different id) — used by the "Copy from" dropdown.
  const { data: siblingsResponse } = useQuery({
    ...propertyQueries.list({
      filters: ownerId ? { organization: ownerId } : undefined,
      pagination: new Pagination({ size: 100 }),
    }),
    enabled: isColombia && Boolean(ownerId),
  })

  const copyableSiblings = useMemo(() => {
    const all = siblingsResponse?.results ?? []
    return all.filter((r) => {
      if (r.id === property.id) return false
      return (
        r.traEnabled ||
        r.sireEnabled ||
        Boolean(r.traApiKeyMasked) ||
        Boolean(r.traRntCode) ||
        Boolean(r.traEstablishmentName) ||
        Boolean(r.sireEstablishmentCode)
      )
    })
  }, [siblingsResponse, property.id])

  const handleCopyFromSibling = (e: ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value
    if (!id) return
    const sibling = copyableSiblings.find((r) => r.id === id)
    if (!sibling) return
    setForm({
      traEnabled: Boolean(sibling.traEnabled),
      traApiKey: '',
      traRntCode: sibling.traRntCode ?? '',
      traEstablishmentName: sibling.traEstablishmentName ?? '',
      traAccommodationType: sibling.traAccommodationType ?? '',
      sireEnabled: Boolean(sibling.sireEnabled),
      sireEstablishmentCode: sibling.sireEstablishmentCode ?? '',
      sireCityCode: null,
    })
    e.target.value = ''
  }

  const saveMutation = useMutation({
    mutationFn: () => {
      const data: Record<string, unknown> = {
        traEnabled: form.traEnabled,
        traRntCode: form.traRntCode || null,
        traEstablishmentName: form.traEstablishmentName || null,
        traAccommodationType: form.traAccommodationType || null,
        sireEnabled: form.sireEnabled,
        sireEstablishmentCode: form.sireEstablishmentCode || null,
        sireCityCode: form.sireCityCode,
      }
      if (form.traApiKey.trim().length > 0) {
        data.traApiKey = form.traApiKey.trim()
      }
      return propertyApi.update({ id: property.id, ...data })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: propertyQueries.all() })
      toast.success(t('reporting.saved'))
      setForm((prev) => ({ ...prev, traApiKey: '' }))
    },
    onError: (error: unknown) => {
      if (isAxiosError(error) && error.response?.status === 400 && error.response.data) {
        const data = error.response.data as Record<string, string[] | string>
        const firstKey = Object.keys(data)[0]
        if (firstKey) {
          const raw = data[firstKey]
          const msg = Array.isArray(raw) ? raw[0] : raw
          toast.error(`${firstKey}: ${msg}`)
          return
        }
      }
      toast.error(t('reporting.saveError'))
    },
  })

  // Only show reporting for Colombian properties.
  if (!isColombia) return null

  return (
    <div className="rounded-lg border border-border bg-surface p-4">
      <div className="mb-1 flex items-center justify-between">
        <h4 className="text-sm font-medium text-text-primary">
          {t('reporting.title')}
          <InfoHint text={t('reporting.description')} className="ml-1.5" />
        </h4>
        <CurrentReportingBadge property={property} />
      </div>
      <AssistantHint
        id="property-reporting"
        text={t('assistantHint.propertyReporting')}
        className="mt-2"
      />

      {copyableSiblings.length > 0 && (
        <div className="mt-4">
          <label className="mb-1 block text-sm font-medium text-primary">
            {t('reporting.copyFromProperty')}
            <InfoHint text={t('reporting.copyFromHint')} className="ml-1.5" />
          </label>
          <select
            defaultValue=""
            onChange={handleCopyFromSibling}
            className="w-full rounded-md border bg-surface p-2 text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-strong"
            data-testid="copy-from-sibling"
          >
            <option value="">{t('reporting.copyFromPlaceholder')}</option>
            {copyableSiblings.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name || t('reporting.unnamedProperty')}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* TRA section */}
      <fieldset className="mt-5 rounded-md border border-border p-3">
        <legend className="px-1 text-sm font-semibold text-text-primary">
          {t('reporting.traSection')}
        </legend>
        <a
          href={reportingGuidePath(i18next.language, 'tra')}
          target="_blank"
          rel="noreferrer noopener"
          className="mt-1 inline-block text-xs text-primary underline hover:text-primary-400"
          data-testid="tra-help-link"
        >
          {t('reporting.howDoIGetThese')}
        </a>
        <label className="mt-1 flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={form.traEnabled}
            onChange={(e) => setForm((p) => ({ ...p, traEnabled: e.target.checked }))}
            className="h-4 w-4 rounded border-border-strong text-primary focus:ring-primary"
            data-testid="tra-enabled"
          />
          {t('reporting.enableTra')}
        </label>

        {form.traEnabled && (
          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <Input
                type="password"
                label={t('reporting.apiKeyLabel')}
                autoComplete="off"
                placeholder={
                  hasExistingMaskedKey
                    ? `${t('reporting.apiKeyExistingPrefix')}${property.traApiKeyMasked}`
                    : t('reporting.apiKeyPlaceholder')
                }
                value={form.traApiKey}
                onChange={(e) => setForm((p) => ({ ...p, traApiKey: e.target.value }))}
                data-testid="tra-api-key"
              />
              <TraTokenLink />
              {hasExistingMaskedKey && (
                <p className="mt-1 text-xs text-text-placeholder">
                  {t('reporting.apiKeyKeepHint', { masked: property.traApiKeyMasked })}
                </p>
              )}
            </div>
            <div>
              <Input
                label={t('reporting.rntLabel')}
                placeholder={t('reporting.rntPlaceholder')}
                value={form.traRntCode}
                onChange={(e) => setForm((p) => ({ ...p, traRntCode: e.target.value }))}
                data-testid="tra-rnt-code"
              />
            </div>
            <div>
              <Input
                label={t('reporting.establishmentNameLabel')}
                placeholder={t('reporting.establishmentNamePlaceholder')}
                value={form.traEstablishmentName}
                onChange={(e) => setForm((p) => ({ ...p, traEstablishmentName: e.target.value }))}
                data-testid="tra-establishment-name"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-primary">
                {t('reporting.accommodationTypeLabel')}
              </label>
              <select
                value={form.traAccommodationType}
                onChange={(e) => setForm((p) => ({ ...p, traAccommodationType: e.target.value }))}
                className="w-full rounded-md border bg-surface p-2 text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-strong"
                data-testid="tra-accommodation-type"
              >
                {ACCOMMODATION_TYPES.map((o) => (
                  <option key={o.value || 'default'} value={o.value}>
                    {o.value ? o.label : t('reporting.onboarding.accommodationTypeDefault')}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </fieldset>

      {/* SIRE section */}
      <SireSection property={property} form={form} setForm={setForm} />

      <div className="mt-5 flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="primary"
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          data-testid="save-reporting"
        >
          {saveMutation.isPending ? t('reporting.saving') : t('common.save')}
        </Button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// SIRE city resolution status + picker
// ---------------------------------------------------------------------------

type SireCityState = 'resolved' | 'unresolved' | 'overridden'

function deriveSireCityState(
  sireCityCode: string | null,
  sireCityCodeResolved: string | null | undefined,
): SireCityState {
  if (sireCityCode) return 'overridden'
  if (sireCityCodeResolved) return 'resolved'
  return 'unresolved'
}

const SireSection = ({
  property,
  form,
  setForm,
}: {
  property: Property
  form: ReportingFormState
  setForm: React.Dispatch<React.SetStateAction<ReportingFormState>>
}) => {
  const { t } = useTranslation()
  const [showPicker, setShowPicker] = useState(false)
  const [selectedCity, setSelectedCity] = useState<SelectOption[]>([])

  const cityState = deriveSireCityState(form.sireCityCode, property.sireCityCodeResolved)
  const resolvedCode = property.sireCityCodeResolved
  const resolvedName = property.sireCityNameResolved

  // Disable sire_enabled when city is unresolved and no override
  const sireToggleDisabled = cityState === 'unresolved'

  const { data: sireCitiesData, isLoading: loadingCities } = useQuery(propertyQueries.sireCities())

  const cityOptions: SelectOption[] = useMemo(
    () =>
      (sireCitiesData?.cities ?? []).map((c) => ({
        label: `${c.name} (${c.code})`,
        value: c.code,
      })),
    [sireCitiesData],
  )

  const handleOverrideSave = (values: SelectOption[]) => {
    setSelectedCity(values)
    if (values.length > 0) {
      setForm((p) => ({ ...p, sireCityCode: values[0].value }))
      setShowPicker(false)
    }
  }

  const handleClearOverride = () => {
    setSelectedCity([])
    setForm((p) => ({ ...p, sireCityCode: null }))
    setShowPicker(false)
  }

  return (
    <fieldset className="mt-4 rounded-md border border-border p-3" data-testid="sire-section">
      <legend className="px-1 text-sm font-semibold text-text-primary">
        {t('reporting.sireSection')}
      </legend>
      <a
        href={reportingGuidePath(i18next.language, 'sire')}
        target="_blank"
        rel="noreferrer noopener"
        className="mt-1 inline-block text-xs text-primary underline hover:text-primary-400"
        data-testid="sire-help-link"
      >
        {t('reporting.howDoIGetThese')}
      </a>

      {/* SIRE city resolution status */}
      <div className="mt-2" data-testid="sire-city-status">
        {cityState === 'resolved' && !showPicker && (
          <div className="flex items-center gap-2 rounded-md bg-green-50 px-3 py-2 text-sm text-green">
            <span data-testid="sire-city-resolved">
              {t('reporting.sireCityResolved', {
                name: resolvedName,
                code: resolvedCode,
              })}
            </span>
            <Button
              variant="text"
              tone="primary"
              type="button"
              onClick={() => setShowPicker(true)}
              className="ml-auto text-xs underline"
              data-testid="sire-city-override-link"
            >
              {t('reporting.sireCityOverrideLink')}
            </Button>
          </div>
        )}

        {cityState === 'overridden' && !showPicker && (
          <div className="flex items-center gap-2 rounded-md bg-accent-50 px-3 py-2 text-sm text-accent">
            <span data-testid="sire-city-overridden">
              {t('reporting.sireCityOverridden', {
                name:
                  sireCitiesData?.cities?.find((c) => c.code === form.sireCityCode)?.name ??
                  resolvedName,
                code: form.sireCityCode ?? resolvedCode,
              })}
            </span>
            <Button
              variant="text"
              tone="primary"
              type="button"
              onClick={handleClearOverride}
              className="ml-auto text-xs underline"
              data-testid="sire-city-clear-override"
            >
              {t('reporting.sireCityClearOverride')}
            </Button>
          </div>
        )}

        {(cityState === 'unresolved' || showPicker) && (
          <div data-testid="sire-city-picker-container">
            {cityState === 'unresolved' && (
              <p
                className="mb-2 rounded-md bg-error-50 px-3 py-2 text-sm text-error"
                data-testid="sire-city-unresolved"
              >
                {t('reporting.sireCityUnresolved')}
              </p>
            )}
            <CustomSelect
              searchable
              clearable
              options={cityOptions}
              values={selectedCity}
              loading={loadingCities}
              placeholder={t('reporting.sireCityPickerPlaceholder')}
              onChange={handleOverrideSave}
              data-testid="sire-city-picker"
            />
            {showPicker && cityState !== 'unresolved' && (
              <Button
                variant="unstyled"
                type="button"
                onClick={() => setShowPicker(false)}
                className="mt-1 text-xs text-text-placeholder underline"
              >
                {t('common.cancel')}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* SIRE enabled toggle */}
      <div className="relative mt-2">
        <label
          className={cn([
            'flex items-center gap-2 text-sm text-gray-700',
            sireToggleDisabled && 'cursor-not-allowed opacity-50',
          ])}
          title={sireToggleDisabled ? t('reporting.sireEnableBlockedTooltip') : undefined}
        >
          <input
            type="checkbox"
            checked={form.sireEnabled}
            onChange={(e) => setForm((p) => ({ ...p, sireEnabled: e.target.checked }))}
            disabled={sireToggleDisabled}
            className="h-4 w-4 rounded border-border-strong text-primary focus:ring-primary"
            data-testid="sire-enabled"
          />
          {t('reporting.enableSire')}
        </label>
        {sireToggleDisabled && (
          <p className="mt-0.5 text-xs text-text-tertiary">
            {t('reporting.sireEnableBlockedTooltip')}
          </p>
        )}
      </div>

      {form.sireEnabled && (
        <div className="mt-3">
          <Input
            label={t('reporting.sireCodeLabel')}
            placeholder={t('reporting.sireCodePlaceholder')}
            value={form.sireEstablishmentCode}
            onChange={(e) => setForm((p) => ({ ...p, sireEstablishmentCode: e.target.value }))}
            data-testid="sire-establishment-code"
          />
        </div>
      )}
    </fieldset>
  )
}

type ReportingBadgeState = 'on' | 'off' | 'na'

const reportingBadgeState = (property: Property): ReportingBadgeState => {
  if (property.country !== 'CO') return 'na'
  if (property.reportsTra || property.reportsSire) return 'on'
  return 'off'
}

/**
 * "Reporting: ON / OFF / Not Applicable" status pill, reusable across
 * property cards / detail headers.
 */
export const ReportingStatusBadge = ({ property }: { property: Property }) => {
  const { t } = useTranslation()
  const state = reportingBadgeState(property)
  const styles: Record<ReportingBadgeState, string> = {
    on: 'bg-green-50 text-green',
    off: 'bg-gray-100 text-text-secondary',
    na: 'bg-surface-hover text-text-tertiary',
  }
  const labels: Record<ReportingBadgeState, string> = {
    on: t('reporting.badgeOn'),
    off: t('reporting.badgeOff'),
    na: t('reporting.badgeNa'),
  }
  return (
    <span
      className={cn([
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        styles[state],
      ])}
      data-testid={`reporting-badge-${state}`}
    >
      {labels[state]}
    </span>
  )
}

// Internal helper — same visual as ReportingStatusBadge but positioned for the
// card header.
const CurrentReportingBadge = ({ property }: { property: Property }) => (
  <ReportingStatusBadge property={property} />
)
